import type { ResourceAmounts } from '../buildings/definitions';
import type { ArmorClass, DamageType } from '../combat/rules';

export type UnitTypeId = 'ranger' | 'heavy' | 'engineer' | 'walker';

export interface UnitDefinition {
  readonly id: UnitTypeId;
  readonly name: string;
  readonly role: string;
  readonly maxHealth: number;
  /** Tiles per second. */
  readonly speed: number;
  /** Attack reach in tiles (centre to target). */
  readonly range: number;
  readonly damage: number;
  readonly damageType: DamageType;
  /** Seconds between attacks. */
  readonly cooldown: number;
  /** How far it notices enemies (tiles) when idle or attack-moving. */
  readonly sight: number;
  readonly armorClass: ArmorClass;
  /** Flat damage removed from every hit it takes (after multipliers). */
  readonly armor: number;
  /** Ranged units fire projectiles at this speed (tiles/s); melee units hit instantly. */
  readonly projectileSpeed?: number;
  /** Infected type (Phase 7): spawned by the infection system, never trained, never commanded by players. */
  readonly infected?: boolean;
  /** Chase leash in tiles beyond sight (default COMBAT.leashTiles). */
  readonly leash?: number;
  /** A* node budget per route (default 20 000). Infected use a small one so hundreds of them stay cheap. */
  readonly pathNodes?: number;
  /** Health per second it restores when repairing (Phase 8; Engineers). */
  readonly repairRate?: number;
  readonly cost: ResourceAmounts;
  /** Survivors taken from the colony. */
  readonly survivors: number;
  readonly trainSeconds: number;
}

/** Placeholder balance (DECISIONS D57, combat stats D63). */
const DEFS: readonly UnitDefinition[] = [
  {
    id: 'ranger',
    name: 'Ranger',
    role: 'Ranged rifle squad member. Good against the regular infected.',
    maxHealth: 60,
    speed: 2.2,
    range: 4,
    damage: 8,
    damageType: 'bullet',
    cooldown: 1,
    sight: 6,
    armorClass: 'light',
    armor: 0,
    projectileSpeed: 14,
    cost: { wood: 15, scrap: 10 },
    survivors: 1,
    trainSeconds: 12,
  },
  {
    id: 'heavy',
    name: 'Heavy',
    role: 'Armoured frontline brawler. Slow, tough and hits hard up close.',
    maxHealth: 160,
    speed: 1.4,
    range: 1.3,
    damage: 18,
    damageType: 'melee',
    cooldown: 1.5,
    sight: 5,
    armorClass: 'armored',
    armor: 3,
    cost: { wood: 20, scrap: 30 },
    survivors: 1,
    trainSeconds: 18,
  },
  {
    id: 'engineer',
    name: 'Engineer',
    role: 'Repairs your buildings (tap a damaged one, or it fixes what is nearby). Weak in a fight.',
    maxHealth: 50,
    speed: 2,
    range: 1.3,
    damage: 4,
    damageType: 'melee',
    cooldown: 1.2,
    sight: 4,
    repairRate: 8,
    armorClass: 'light',
    armor: 0,
    cost: { wood: 20, scrap: 10 },
    survivors: 1,
    trainSeconds: 14,
  },
  {
    id: 'walker',
    name: 'Walker',
    role: 'Infected. Slow and relentless; bites spread the infection.',
    maxHealth: 80,
    speed: 0.9,
    range: 1.2,
    damage: 6,
    damageType: 'melee',
    cooldown: 1.2,
    sight: 7,
    armorClass: 'light',
    armor: 0,
    cost: {},
    survivors: 0,
    trainSeconds: 0,
    infected: true,
    leash: 6,
    pathNodes: 1200,
  },
];

export const UNIT_DEFINITIONS: ReadonlyMap<UnitTypeId, UnitDefinition> = new Map(DEFS.map((d) => [d.id, d]));
export const UNIT_TYPES: readonly UnitTypeId[] = DEFS.map((d) => d.id);
/** Types players can own and train. */
export const PLAYER_UNIT_TYPES: readonly UnitTypeId[] = DEFS.filter((d) => !d.infected).map((d) => d.id);

export function unitDef(id: UnitTypeId): UnitDefinition {
  const d = UNIT_DEFINITIONS.get(id);
  if (!d) throw new Error(`Unknown unit type "${id}"`);
  return d;
}

export function isUnitTypeId(v: unknown): v is UnitTypeId {
  return typeof v === 'string' && UNIT_DEFINITIONS.has(v as UnitTypeId);
}

/** Most units a single building can have queued. */
export const TRAIN_QUEUE_LIMIT = 5;
/** Most units one move / attack command may address. */
export const MAX_UNITS_PER_COMMAND = 200;

/** Unit combat posture. Aggressive: engage enemies in sight. Hold: never move on its own; only shoot what is in range. */
export type Stance = 'aggressive' | 'hold';
export const STANCES: readonly Stance[] = ['aggressive', 'hold'];
